import { getStateCode } from "./state";

const LETTER_PATTERN = /\p{L}/u;

function normalizeStateCodeInput(
  stateCode: string | undefined | null,
): string | undefined | null {
  if (!stateCode) return stateCode;
  const trimmed = stateCode.trim();
  return getStateCode(trimmed) ?? trimmed;
}

function normalizeCityNameForPairKey(cityName: string): string {
  return (normalizeCityName(cityName) ?? cityName)
    .replace(/[.-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export const GOOGLE_CITY_COMPONENT_TYPES = [
  "locality",
  "postal_town",
  "sublocality_level_1",
  "sublocality",
] as const;

export const GOOGLE_CITY_FALLBACK_COMPONENT_TYPES = [
  ...GOOGLE_CITY_COMPONENT_TYPES,
  "administrative_area_level_3",
  "neighborhood",
] as const;

export function normalizeCityName(
  raw: string | undefined | null,
): string | undefined {
  if (!raw) return undefined;
  const normalized = raw.replace(/\s+/g, " ").replace(/^[\s,;]+|[\s,;]+$/g, "");
  return normalized.length > 0 ? normalized : undefined;
}

export function cityLookupKey(name: string): string {
  const cleaned = name
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\u02B0-\u02FF]/g, "")
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .trim()
    .toLowerCase()
    .replace(/\./g, "")
    .replace(/\s+/g, " ");
  return cleaned
    .replace(/^st\s+/, "saint ")
    .replace(/^ft\s+/, "fort ")
    .replace(/^mt\s+/, "mount ");
}

export function toCitySlug(cityName: string): string {
  return (
    cityName
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[\u02B0-\u02FF]/g, "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "")
  );
}

const CANONICAL_SLUG = /^[a-z0-9]+(-[a-z0-9]+)*$/;

const COUNTRY_NAME_SLUGS = new Set([
  "us",
  "usa",
  "u-s",
  "u-s-a",
  "america",
  "united-states",
  "united-states-of-america",
]);

const PLACEHOLDER_NAME_SLUGS = new Set([
  "n-a",
  "nan",
  "none",
  "null",
  "undefined",
  "unknown",
  "tbd",
  "tba",
  "not-applicable",
  "not-available",
]);

export function isValidCitySlug(slug: string | undefined | null): boolean {
  if (!slug) return false;
  const trimmed = slug.trim();
  if (trimmed.length < 3) return false;
  if (!LETTER_PATTERN.test(trimmed)) return false;
  if (COUNTRY_NAME_SLUGS.has(trimmed)) return false;
  if (isPlaceholderSlug(trimmed)) return false;
  return CANONICAL_SLUG.test(trimmed);
}

function isPlaceholderSlug(slug: string): boolean {
  return (
    PLACEHOLDER_NAME_SLUGS.has(slug) ||
    PLACEHOLDER_NAME_SLUGS.has(slug.replace(/-[a-z]{2}$/, ""))
  );
}

export function stripLegacyStateSuffix(
  citySlug: string | undefined,
  stateCode: string | undefined,
): string | undefined {
  if (!citySlug || !stateCode) return undefined;
  const suffix = `-${stateCode.toLowerCase()}`;
  if (!citySlug.endsWith(suffix)) return undefined;
  return citySlug.slice(0, -suffix.length) || undefined;
}

const KNOWN_INVALID_CITY_STATE_PAIRS = new Set([
  "klkl|tx",
  "wyoming|wy",
  "louisiana|la",
  "florida|fl",
  "new jersey|nj",
  "new jersey|pa",
  "new york|ga",
  "illinois|hi",
  "florida|al",
]);

export function isKnownInvalidCityStatePair(
  cityName: string,
  stateCode: string | undefined | null,
): boolean {
  const normalizedStateCode = normalizeStateCodeInput(stateCode);
  if (!normalizedStateCode) return false;
  const normalizedCityName = normalizeCityNameForPairKey(cityName);
  const key = `${normalizedCityName.trim().toLowerCase()}|${normalizedStateCode.trim().toLowerCase()}`;
  return KNOWN_INVALID_CITY_STATE_PAIRS.has(key);
}

const KNOWN_VALID_CITY_STATE_OVERRIDE_PAIRS: ReadonlyArray<{
  name: string;
  stateCode: string;
}> = [
  { name: "america", stateCode: "il" },
  { name: "america", stateCode: "in" },
];

const KNOWN_VALID_CITY_STATE_OVERRIDES_BY_NAME = new Set(
  KNOWN_VALID_CITY_STATE_OVERRIDE_PAIRS.map(
    (pair) => `${pair.name}|${pair.stateCode}`,
  ),
);

const KNOWN_VALID_CITY_STATE_OVERRIDES_BY_SLUG = new Set(
  KNOWN_VALID_CITY_STATE_OVERRIDE_PAIRS.map(
    (pair) => `${toCitySlug(pair.name)}|${pair.stateCode}`,
  ),
);

export function isKnownValidCityStatePair(
  cityName: string,
  stateCode: string | undefined | null,
): boolean {
  const normalizedStateCode = normalizeStateCodeInput(stateCode);
  if (!normalizedStateCode) return false;
  const normalizedCityName = normalizeCityNameForPairKey(cityName);
  const key = `${normalizedCityName.trim().toLowerCase()}|${normalizedStateCode.trim().toLowerCase()}`;
  return KNOWN_VALID_CITY_STATE_OVERRIDES_BY_NAME.has(key);
}

export function isKnownValidCityStateSlugPair(
  citySlug: string,
  stateCode: string | undefined | null,
): boolean {
  if (!stateCode) return false;
  const key = `${citySlug.trim().toLowerCase()}|${stateCode.trim().toLowerCase()}`;
  return KNOWN_VALID_CITY_STATE_OVERRIDES_BY_SLUG.has(key);
}

export function isInvalidCityRecord(record: {
  name: string;
  slug: string;
  stateCode?: string | null;
}): boolean {
  const overridden = isKnownValidCityStatePair(record.name, record.stateCode);
  const canonicalNameSlug = toCitySlug(record.name);
  const storedSlugInvalid =
    !isValidCitySlug(record.slug) &&
    !(overridden && record.slug === canonicalNameSlug);
  const shapeInvalid =
    storedSlugInvalid || (!overridden && !isValidCitySlug(canonicalNameSlug));
  return (
    shapeInvalid ||
    (!overridden &&
      isKnownInvalidCityStatePair(record.name, record.stateCode))
  );
}

export function isInvalidCityStateText(
  cityName: string,
  stateCode: string | undefined | null,
): boolean {
  if (!cityName.trim() || !stateCode?.trim()) return false;
  const overridden = isKnownValidCityStatePair(cityName, stateCode);
  const shapeInvalid = !overridden && !isValidCitySlug(toCitySlug(cityName));
  return (
    shapeInvalid ||
    (!overridden && isKnownInvalidCityStatePair(cityName, stateCode))
  );
}
